<template>
	<v-card data-testid = "dashboard-pie-events-widget" dark outlined class = "dashboard-pie-events-widget fill-height">
		<v-system-bar lights-out dark class = "vue-draggable-handle" style = "cursor:move">
			<v-icon small color = "deep-purple lighten-2">mdi-chart-pie</v-icon>
			<span class = "white--text subtitle-2 text-uppercase ml-1 text-truncate">
				{{ widgetTitle }}
			</span>
			<v-spacer></v-spacer>
			<span v-if = "generatedAt" class = "caption grey--text text-truncate mr-2">
				{{ $ml.get('dashboard_pie_events_generated_at') }}: {{ generatedAt }}
			</span>
			<v-btn icon x-small class = "no-drag" :disabled = "loading" @click = "refresh">
				<v-icon x-small color = "white">mdi-refresh</v-icon>
			</v-btn>
			<v-btn icon x-small class = "error ml-1 no-drag" @click = "$emit('close_component', layout_id)">
				<v-icon x-small color = "white">mdi-close</v-icon>
			</v-btn>
		</v-system-bar>

		<div class = "dashboard-pie-events-widget__body no-drag">
			<div class = "dashboard-pie-events-widget__intervals">
				<span class = "caption grey--text text-uppercase">{{ $ml.get('dashboard_operator_action_time_interval') }}</span>
				<v-btn-toggle v-model = "selectedInterval" mandatory dense dark class = "dashboard-pie-events-widget__interval-toggle">
					<v-btn v-for = "option in intervalOptions" :key = "option.value" x-small :value = "option.value" :disabled = "loading">
						{{ $ml.get(option.labelKey) }}
					</v-btn>
				</v-btn-toggle>
			</div>

			<v-overlay v-if = "loading && !hasChartData" absolute z-index = "10">
				<div class = "text-center">
					<v-progress-circular indeterminate size = "48" color = "deep-purple lighten-2"></v-progress-circular>
					<div class = "mt-3 caption grey--text">{{ $ml.get('dashboard_pie_events_loading_data') }}</div>
				</div>
			</v-overlay>

			<v-alert v-if = "unsupportedSchema" type = "warning" dense prominent class = "ma-2">
				{{ $ml.get('dashboard_pie_events_unsupported_config') }}
			</v-alert>

			<v-alert v-else-if = "error" type = "error" dense prominent class = "ma-2">
				<v-row align = "center" no-gutters>
					<v-col class = "grow">{{ error }}</v-col>
					<v-col class = "shrink">
						<v-btn small outlined color = "white" class = "ml-3" @click = "refresh">
							<v-icon small left>mdi-refresh</v-icon>
							{{ $ml.get('dashboard_pie_events_retry') }}
						</v-btn>
					</v-col>
				</v-row>
			</v-alert>

			<div v-show = "!unsupportedSchema && !error" class = "dashboard-pie-events-widget__chart-wrap">
				<canvas ref = "pieCanvas"></canvas>
				<div v-if = "hasChartData && isPieLikeChart" data-testid = "dashboard-pie-events-widget-chart-total" class = "dashboard-pie-events-widget__chart-total">
					<div>{{ $ml.get('dashboard_pie_events_interval_total') }}</div>
					<strong>{{ chartData.total }}</strong>
				</div>

				<div v-if = "!loading && !hasChartData" class = "dashboard-pie-events-widget__empty text-center">
					<v-icon large color = "grey lighten-1">mdi-chart-pie</v-icon>
					<div class = "caption grey--text mt-2">{{ $ml.get('dashboard_pie_events_empty_data') }}</div>
				</div>
			</div>

			<v-divider v-if = "hasChartData" class = "my-2"></v-divider>

			<div v-if = "hasChartData" data-testid = "dashboard-pie-events-widget-legend" class = "dashboard-pie-events-widget__legend">
				<div v-for = "item in legendItems" :key = "item.label" class = "dashboard-pie-events-widget__legend-item">
					<span class = "dashboard-pie-events-widget__legend-swatch" :style = "{ backgroundColor: item.color }"></span>
					<span class = "dashboard-pie-events-widget__legend-label">{{ item.label }}</span>
					<strong>{{ item.value }}</strong>
					<span class = "dashboard-pie-events-widget__legend-percent">{{ item.percent }}%</span>
				</div>
			</div>

			<div v-if = "hasChartData" data-testid = "dashboard-pie-events-widget-footer" class = "dashboard-pie-events-widget__footer">
				<div class = "caption grey--text">
					{{ $ml.get('dashboard_pie_events_interval_total') }}: <strong class = "white--text">{{ chartData.total }}</strong>
				</div>
				<div v-if = "chartData.visibleTotal !== null && chartData.visibleTotal !== chartData.total" class = "caption grey--text">
					{{ $ml.get('dashboard_pie_events_grouped_total') }}: <strong class = "white--text">{{ chartData.visibleTotal }}</strong>
				</div>
				<div class = "caption grey--text">
					{{ $ml.get('dashboard_pie_events_refresh_seconds') }}: {{ refreshSeconds }}
				</div>
			</div>
		</div>
	</v-card>
</template>

<script>
import Chart from 'chart.js';
import DashboardChartsService from '@/services/dashboard-charts.service.js';
import {
	DASHBOARD_PIE_EVENTS_SCHEMA_VERSION,
	DASHBOARD_PIE_EVENTS_DEFAULT_REFRESH_SECONDS,
	DASHBOARD_PIE_EVENTS_DEFAULT_CHART_TYPE,
	DASHBOARD_PIE_EVENTS_SUPPORTED_CHART_TYPES,
	DASHBOARD_CHART_INTERVAL_OPTIONS,
	buildDashboardChartTimeRange,
	normalizeDashboardChartIntervalPreset,
} from '@/services/dashboard-pie-events.config.js';

const DEFAULT_COLORS = [
	'#7E57C2', '#42A5F5', '#26A69A', '#66BB6A', '#FFCA28',
	'#FFA726', '#EF5350', '#EC407A', '#AB47BC', '#78909C',
];

export default {
	name : 'dashboard_pie_events_widget',

	props : ['layout', 'layout_id', 'hosts', 'info_to_component', 'layout_end'],

	data(){
		return {
			chart : null,
			chartData : {
				labels : [],
				values : [],
				colors : [],
				total  : 0,
				visibleTotal: null,
			},
			loading : false,
			error : null,
			generatedAt : null,
			refreshTimer : null,
			selectedInterval : this.getInitialInterval(),
		};
	},

	computed : {
		intervalOptions(){
			return DASHBOARD_CHART_INTERVAL_OPTIONS;
		},
		streamData(){
			return this.info_to_component || this.hosts || {};
		},
		timezone(){
			const config = this.streamData && this.streamData.config ? this.streamData.config : {};
			return config.time && config.time.custom && config.time.custom.tz ? config.time.custom.tz : 'America/Lima';
		},
		requestStreamData(){
			const config = this.streamData && this.streamData.config ? this.streamData.config : {};
			return Object.assign({}, this.streamData, {
				config: Object.assign({}, config, {
					time: buildDashboardChartTimeRange(this.selectedInterval, this.timezone),
				}),
			});
		},
		unsupportedSchema(){
			return !this.streamData || this.streamData.schemaVersion !== DASHBOARD_PIE_EVENTS_SCHEMA_VERSION;
		},
		widgetTitle(){
			const config = this.streamData && this.streamData.config ? this.streamData.config : {};
			return (this.streamData && this.streamData.title) || config.name || config.title || this.$ml.get('dashboard_pie_events_title');
		},
		chartType(){
			const config = this.streamData && this.streamData.config ? this.streamData.config : {};
			const chartType = (this.streamData && this.streamData.chartType) || config.chartType || DASHBOARD_PIE_EVENTS_DEFAULT_CHART_TYPE;
			return DASHBOARD_PIE_EVENTS_SUPPORTED_CHART_TYPES.includes(chartType) ? chartType : DASHBOARD_PIE_EVENTS_DEFAULT_CHART_TYPE;
		},
		isPieLikeChart(){
			return ['pie', 'doughnut'].includes(this.chartType);
		},
		isLineChart(){
			return this.chartType === 'line';
		},
		legendItems(){
			const total = Number(this.chartData.total || 0);
			return this.chartData.labels.map((label, index) => {
				const value = Number(this.chartData.values[index] || 0);
				return {
					label,
					value,
					color: this.chartData.colors[index] || DEFAULT_COLORS[index % DEFAULT_COLORS.length],
					percent: total > 0 ? Number(((value / total) * 100).toFixed(1)) : 0,
				};
			});
		},
		refreshSeconds(){
			const config = this.streamData && this.streamData.config ? this.streamData.config : {};
			const value = Number(config.refreshSeconds || this.streamData.refreshSeconds || DASHBOARD_PIE_EVENTS_DEFAULT_REFRESH_SECONDS);
			return value > 0 ? value : DASHBOARD_PIE_EVENTS_DEFAULT_REFRESH_SECONDS;
		},
		hasChartData(){
			return this.chartData && this.chartData.labels.length > 0 && this.chartData.values.some(value => Number(value) > 0);
		},
	},

	watch : {
		streamData : {
			handler(){
				this.resetWidget();
			},
			deep : true,
		},
		selectedInterval(){
			this.refresh();
		},
		layout_end(){
			this.resizeChart();
		},
		'$ml.current'(){
			this.updateChart();
		},
	},

	mounted(){
		this.$nextTick(() => {
			this.createChart();
			this.loadInitialData();
		});
	},

	beforeDestroy(){
		this.clearRefreshTimer();
		this.destroyChart();
	},

	methods : {
		loadInitialData(){
			if (this.unsupportedSchema) return;
			if (this.streamData.preview) {
				this.chartData = this.normalizeChartData(this.streamData.preview);
				this.updateChart();
			}
			this.refresh();
			this.startRefreshTimer();
		},
		resetWidget(){
			this.clearRefreshTimer();
			this.error = null;
			this.generatedAt = null;
			this.chartData = { labels: [], values: [], colors: [], total: 0, visibleTotal: null };
			this.selectedInterval = this.getInitialInterval();
			this.updateChart();
			this.loadInitialData();
		},
		refresh(){
			if (this.unsupportedSchema || this.loading) return;
			this.loading = true;
			this.error = null;

			DashboardChartsService.getPieEventsData(this.requestStreamData).then((response) => {
				if (response && (response.isAxiosError || response.response)) {
					this.error = this.getErrorMessage(response);
					return;
				}
				const data = this.unwrapResponse(response);
				this.chartData = this.normalizeChartData(data);
				this.generatedAt = data && (data.generatedAt || data.generated_at) ? (data.generatedAt || data.generated_at) : null;
				this.updateChart();
				this.restartRefreshTimer(data && (data.nextRefreshSeconds || data.next_refresh_seconds));
			}).catch(() => {
				this.error = this.$ml.get('dashboard_pie_events_data_error');
			}).finally(() => {
				this.loading = false;
			});
		},
		unwrapResponse(response){
			if (!response) return null;
			if (response.data && response.data.data) return response.data.data;
			if (response.data) return response.data;
			return response;
		},
		getInitialInterval(){
			const config = this.info_to_component && this.info_to_component.config ? this.info_to_component.config : {};
			const preset = normalizeDashboardChartIntervalPreset(config.time && config.time.preset ? config.time.preset : 'today');
			return DASHBOARD_CHART_INTERVAL_OPTIONS.some(option => option.value === preset) ? preset : 'today';
		},
		getErrorMessage(response){
			const data = response && response.response ? response.response.data : null;
			const error = data && data.error ? data.error : null;
			return (error && error.message) || this.$ml.get('dashboard_pie_events_data_error');
		},
			normalizeChartData(data){
				const chart = data && data.chart ? data.chart : data;
				if (!chart) return { labels: [], values: [], colors: [], total: 0, visibleTotal: null };

				if (this.isLineChart) {
					const linePoints = this.extractLinePoints(chart);
					if (linePoints.length) {
						const values = linePoints.map(point => point.value);
						const visibleTotal = values.reduce((sum, value) => sum + value, 0);
						return {
							labels: linePoints.map(point => point.label),
							values,
							colors: linePoints.map((point, index) => DEFAULT_COLORS[index % DEFAULT_COLORS.length]),
							total: this.resolveIntervalTotal(chart, visibleTotal),
							visibleTotal: this.resolveVisibleTotal(chart, visibleTotal),
						};
					}
				}

				if (Array.isArray(chart.slices)) {
					const labels = chart.slices.map((slice, index) => this.resolveSliceLabel(slice, index));
					const values = chart.slices.map(slice => Number(slice.value || 0));
					const visibleTotal = values.reduce((sum, value) => sum + value, 0);
					return {
						labels,
					values,
					colors : chart.slices.map((slice, index) => slice.color || DEFAULT_COLORS[index % DEFAULT_COLORS.length]),
					total  : this.resolveIntervalTotal(chart, visibleTotal),
					visibleTotal: this.resolveVisibleTotal(chart, visibleTotal),
				};
			}

			const labels = Array.isArray(chart.labels) ? chart.labels : [];
			const values = Array.isArray(chart.values) ? chart.values.map(value => Number(value || 0)) : [];
			const visibleTotal = values.reduce((sum, value) => sum + value, 0);
			return {
				labels,
				values,
				colors : Array.isArray(chart.colors) && chart.colors.length ? chart.colors : labels.map((label, index) => DEFAULT_COLORS[index % DEFAULT_COLORS.length]),
				total  : this.resolveIntervalTotal(chart, visibleTotal),
				visibleTotal: this.resolveVisibleTotal(chart, visibleTotal),
				};
			},
			extractLinePoints(chart){
				const sources = [];
				const timeseries = chart.timeseries || chart.timeSeries;
				if (Array.isArray(timeseries)) sources.push(timeseries);
				else if (timeseries && typeof timeseries === 'object') {
					if (Array.isArray(timeseries.points)) sources.push({ labels: timeseries.labels, points: timeseries.points });
					if (Array.isArray(timeseries.series) && timeseries.series[0] && Array.isArray(timeseries.series[0].points)) {
						sources.push({ labels: timeseries.labels, points: timeseries.series[0].points });
					}
				}
				if (Array.isArray(chart.points)) sources.push(chart.points);
				if (Array.isArray(chart.series) && chart.series[0] && Array.isArray(chart.series[0].points)) sources.push({ labels: chart.labels, points: chart.series[0].points });
				if (Array.isArray(chart.labels) && Array.isArray(chart.values)) sources.push({ labels: chart.labels, points: chart.values });

				for (const source of sources) {
					const normalized = this.normalizeLinePointSource(source);
					if (normalized.length) return normalized;
				}
				return [];
			},
			normalizeLinePointSource(source){
				const labels = source && !Array.isArray(source) && Array.isArray(source.labels) ? source.labels : [];
				const points = source && !Array.isArray(source) && Array.isArray(source.points) ? source.points : source;
				if (!Array.isArray(points)) return [];

				return points.map((point, index) => {
					if (Array.isArray(point)) {
						return { label: point[0] || labels[index] || `${index + 1}`, value: Number(point[1] || 0) };
					}
					if (point && typeof point === 'object') {
						const label = point.label || point.time || point.timestamp || point.date || point.bucket || labels[index] || `${index + 1}`;
						const rawValue = point.value !== undefined ? point.value : (point.count !== undefined ? point.count : point.total);
						return { label, value: Number(rawValue || 0) };
					}
					return { label: labels[index] || `${index + 1}`, value: Number(point || 0) };
				}).filter(point => point.label !== undefined && point.label !== null);
			},
			resolveIntervalTotal(chart, fallback){
				return this.normalizeTotalValue(this.firstAvailableTotal(chart, ['requestedTotal', 'requested_total', 'total_requested', 'totalRequested', 'intervalTotal', 'interval_total', 'total']), fallback);
			},
			resolveVisibleTotal(chart, fallback){
				return this.normalizeTotalValue(this.firstAvailableTotal(chart, ['visibleTotal', 'visible_total', 'groupedTotal', 'grouped_total', 'foundTotal', 'found_total']), fallback);
			},
			firstAvailableTotal(source, keys){
				return keys.map(key => source[key]).find(value => value !== undefined && value !== null && value !== '');
			},
			normalizeTotalValue(value, fallback = 0){
				if (value === undefined || value === null || value === '') return fallback;
				const numeric = Number(value);
				return Number.isFinite(numeric) ? numeric : value;
			},
			resolveSliceLabel(slice, index){
				if (slice && slice.labelKey) {
					const translated = this.safeMlGet(slice.labelKey);
					if (translated) return translated;
				}
				const groupedValue = this.resolveGroupedSliceLabel(slice);
				if (groupedValue) return groupedValue;

				const value = slice && (slice.label || slice.name || slice.key || slice.category || slice.name_categorie || slice.motive_categorie || slice.event_type || slice.group || slice.group_by);
				if (value !== undefined && value !== null && `${value}`.trim()) return `${value}`;
				return `${this.$ml.get('dashboard_pie_events_slice')} ${index + 1}`;
			},
			safeMlGet(key){
				try {
					const value = this.$ml.get(key);
					return value && value !== key ? value : null;
				} catch (error) {
					return null;
				}
			},
			resolveGroupedSliceLabel(slice){
				if (!slice) return '';

				const config = this.streamData && this.streamData.config ? this.streamData.config : {};
				const groupBy = Array.isArray(config.groupBy)
					? config.groupBy
					: (Array.isArray(config.group_by) ? config.group_by : []);
				const groupSource = slice.group || slice.group_by || slice.groups || slice.keys || slice.dimensions || slice.values;
				const parts = groupBy.map((field, index) => {
					let value;
					if (slice[field] !== undefined) value = slice[field];
					else if (groupSource && Array.isArray(groupSource)) value = groupSource[index];
					else if (groupSource && typeof groupSource === 'object') value = groupSource[field];

					if (value === undefined || value === null || value === '') return null;
					return `${this.humanizeLabel(field)}: ${this.stringifyGroupValue(value)}`;
				}).filter(Boolean);

				if (parts.length > 0) return parts.join(' / ');
				if (Array.isArray(groupSource)) return groupSource.map(value => this.stringifyGroupValue(value)).filter(Boolean).join(' / ');
				if (groupSource && typeof groupSource === 'object') {
					return Object.keys(groupSource).map(key => `${this.humanizeLabel(key)}: ${this.stringifyGroupValue(groupSource[key])}`).filter(Boolean).join(' / ');
				}

				return '';
			},
			stringifyGroupValue(value){
				if (value === undefined || value === null) return '';
				if (typeof value === 'boolean') return value ? 'Sí' : 'No';
				if (typeof value === 'object') return value.label || value.name || value.key || JSON.stringify(value);
				return `${value}`;
			},
			humanizeLabel(value){
				return `${value}`
					.replace(/([a-z])([A-Z])/g, '$1 $2')
					.replace(/[_-]+/g, ' ')
					.replace(/\s+/g, ' ')
					.trim()
					.replace(/^./, char => char.toUpperCase());
			},
		createChart(){
		if (!this.$refs.pieCanvas || this.chart) return;
		this.syncCanvasSize();
		const ctx = this.$refs.pieCanvas.getContext('2d');
		this.chart = new Chart(ctx, {
			type : this.resolveChartType(),
				data : {
					labels : this.chartData.labels,
					datasets : [{
						data : this.chartData.values,
						backgroundColor : this.isLineChart ? 'rgba(126, 87, 194, 0.16)' : this.chartData.colors,
						borderColor : this.isLineChart ? '#7E57C2' : '#1E1E1E',
						borderWidth : 2,
						pointBackgroundColor : this.isLineChart ? '#42A5F5' : this.chartData.colors,
						pointBorderColor : '#FFFFFF',
						fill : this.isLineChart,
					}],
				},
				options : this.buildChartOptions(),
			});
		},
		resolveChartType(){
			if (this.chartType === 'line') return 'line';
			return this.chartType === 'doughnut' ? 'doughnut' : 'pie';
		},
		buildChartOptions(){
			const options = {
					responsive : false,
					maintainAspectRatio : false,
					legend : {
						display : false,
						labels : { fontColor: '#FFFFFF', boxWidth: 10 },
					},
					tooltips : {
						backgroundColor : '#424242',
						titleFontColor : '#FFFFFF',
						bodyFontColor : '#FFFFFF',
						callbacks : {
							label : (tooltipItem, data) => {
								const label = data.labels[tooltipItem.index] || this.$ml.get('dashboard_pie_events_slice');
								const value = data.datasets[0].data[tooltipItem.index] || 0;
								return `${label}: ${value}`;
							},
						},
					},
			};
			if (this.isLineChart) {
				options.scales = {
					xAxes : [{ ticks: { fontColor: '#FFFFFF' }, gridLines: { color: 'rgba(255,255,255,0.12)' } }],
					yAxes : [{ ticks: { beginAtZero: true, fontColor: '#FFFFFF' }, gridLines: { color: 'rgba(255,255,255,0.12)' } }],
				};
			}
			return options;
		},
		updateChart(){
			this.$nextTick(() => {
				if (!this.chart) this.createChart();
				if (!this.chart) return;
				if (this.syncCanvasSize()) {
					this.destroyChart();
					this.createChart();
				}
				if (!this.chart) return;

				this.chart.data.labels = this.chartData.labels;
				this.chart.data.datasets[0].data = this.chartData.values;
				this.chart.data.datasets[0].backgroundColor = this.isLineChart ? 'rgba(126, 87, 194, 0.16)' : this.chartData.colors;
				this.chart.data.datasets[0].borderColor = this.isLineChart ? '#7E57C2' : '#1E1E1E';
				this.chart.data.datasets[0].pointBackgroundColor = this.isLineChart ? '#42A5F5' : this.chartData.colors;
				this.chart.update();
				this.$emit('resized_state');
			});
		},
		syncCanvasSize(){
			const canvas = this.$refs.pieCanvas;
			if (!canvas) return false;
			const parent = canvas.parentNode;
			const width = Math.max(Math.round(canvas.clientWidth || (parent && parent.clientWidth) || 300), 1);
			const height = Math.max(Math.round(canvas.clientHeight || (parent && parent.clientHeight) || 150), 1);
			const changed = canvas.width !== width || canvas.height !== height;
			if (canvas.width !== width) canvas.width = width;
			if (canvas.height !== height) canvas.height = height;
			return changed;
		},
		resizeChart(){
			this.$nextTick(() => {
				this.syncCanvasSize();
				if (this.chart) this.chart.update();
				this.$emit('resized_state');
			});
		},
		destroyChart(){
			if (this.chart) {
				this.chart.destroy();
				this.chart = null;
			}
		},
		startRefreshTimer(){
			this.clearRefreshTimer();
			this.refreshTimer = setInterval(() => {
				this.refresh();
			}, this.refreshSeconds * 1000);
		},
		restartRefreshTimer(nextRefreshSeconds){
			if (nextRefreshSeconds && Number(nextRefreshSeconds) > 0 && Number(nextRefreshSeconds) !== this.refreshSeconds) {
				this.clearRefreshTimer();
				this.refreshTimer = setInterval(() => {
					this.refresh();
				}, Number(nextRefreshSeconds) * 1000);
			}
		},
		clearRefreshTimer(){
			if (this.refreshTimer) {
				clearInterval(this.refreshTimer);
				this.refreshTimer = null;
			}
		},
	},
};
</script>

<style scoped>
.dashboard-pie-events-widget {
	overflow: hidden;
	background: #1E1E1E;
}

.dashboard-pie-events-widget__body {
	height: calc(100% - 24px);
	padding: 8px;
	overflow: hidden;
	position: relative;
}

.dashboard-pie-events-widget__intervals {
	display: flex;
	justify-content: space-between;
	align-items: center;
	gap: 10px;
	margin-bottom: 6px;
}

.dashboard-pie-events-widget__interval-toggle {
	border: 1px solid rgba(255, 255, 255, 0.14);
	border-radius: 8px;
	overflow: hidden;
}

.dashboard-pie-events-widget__interval-toggle >>> .v-btn {
	min-width: 46px !important;
	padding: 0 8px !important;
	font-size: 10px;
	letter-spacing: 0.08em;
}

.dashboard-pie-events-widget__chart-wrap {
	height: calc(100% - 152px);
	min-height: 120px;
	position: relative;
}

.dashboard-pie-events-widget__chart-wrap canvas {
	display: block;
	width: 100% !important;
	height: 100% !important;
}

.dashboard-pie-events-widget__chart-total {
	position: absolute;
	top: 50%;
	left: 50%;
	transform: translate(-50%, -50%);
	min-width: 86px;
	padding: 7px 9px;
	border-radius: 999px;
	background: rgba(30, 30, 30, 0.78);
	box-shadow: 0 0 0 1px rgba(255, 255, 255, 0.16);
	color: #FFFFFF;
	font-size: 10px;
	line-height: 1.2;
	text-align: center;
	pointer-events: none;
}

.dashboard-pie-events-widget__chart-total strong {
	display: block;
	font-size: 18px;
	line-height: 1.1;
}

.dashboard-pie-events-widget__empty {
	position: absolute;
	top: 50%;
	left: 50%;
	transform: translate(-50%, -50%);
	width: 100%;
}

.dashboard-pie-events-widget__legend {
	display: grid;
	grid-template-columns: repeat(auto-fit, minmax(160px, 1fr));
	gap: 6px 10px;
	max-height: 70px;
	overflow: auto;
}

.dashboard-pie-events-widget__legend-item {
	display: grid;
	grid-template-columns: 10px minmax(0, 1fr) auto auto;
	align-items: center;
	gap: 6px;
	font-size: 11px;
	color: rgba(255, 255, 255, 0.78);
}

.dashboard-pie-events-widget__legend-swatch {
	display: block;
	width: 10px;
	height: 10px;
	border-radius: 50%;
}

.dashboard-pie-events-widget__legend-label {
	overflow: hidden;
	text-overflow: ellipsis;
	white-space: nowrap;
}

.dashboard-pie-events-widget__legend-percent {
	color: rgba(255, 255, 255, 0.58);
}

.dashboard-pie-events-widget__footer {
	display: flex;
	justify-content: space-between;
	gap: 8px;
	flex-wrap: wrap;
}
</style>
